import withParams from "@src/components/widgets/HOC/withParams";
import React from "react";
import { WidgetTemplateComponentType } from "@src/interface/Widget";
import { ILandingStandardWidgetSignatureChefParams } from "../interface";
import useCtaAction from "@src/components/widgets/hooks/useCtaAction";
import { useTranslation } from "@src/hooks/useTranslation";
import SushishopVideo from "@src/lib/render-video/SushishopVideo/SushishopVideo";
import useCompositionBox from "@src/components/widgets/hooks/useCompositionBox";
import useBreakpoint from "design-system/src/Components/Grid/hooks/useBreakpoint";
import useSushiShopImage from "@src/lib/render-image/hooks/useSushiShopImage";
import SignatureChefWidget from "design-system/src/Components/WidgetPart/SignatureChefWidget/SignatureChefWidget";

const SignatureChef: WidgetTemplateComponentType =
  withParams<ILandingStandardWidgetSignatureChefParams>((props) => {
    const { p } = props;
    const { t } = useTranslation(["landing"]);
    const { mobile } = useBreakpoint();

    const title = p("title");
    const tag = p("tag");
    const idProduct = p("id_product") || "";
    const name = p("name");
    const description = p("description");
    const image = p("image");
    const imageMobile = p("image_mobile");
    const video = p("video");
    const videoMobile = p("video_mobile");
    const cta_action = p("cta_action");
    const link = p("link");
    const displayPrice = p("display_price");
    const displayFromMention = p("display_from_mention");

    const { generateImageUrl } = useSushiShopImage();
    const imageUrl = image ? generateImageUrl({ id: image }) : undefined;
    const imageMobileUrl = imageMobile
      ? generateImageUrl({ id: imageMobile })
      : undefined;
    const { generateClick, getSubmitLabel } = useCtaAction(idProduct);
    const { prepareProduct } = useCompositionBox();
    const productModel = idProduct ? prepareProduct(idProduct) : null;

    const handleClick = generateClick(
      cta_action && cta_action === "open_link"
        ? {
            type: cta_action,
            link: link || "#",
          }
        : {
            type: cta_action || "product_card",
          }
    );

    const shouldDisplayPrice =
      displayPrice === true || displayPrice === "1" || displayPrice === "true";

    const fromMentionLabel =
      displayFromMention === true ||
      displayFromMention === "1" ||
      displayFromMention === "true"
        ? t("À partir de", "landing")
        : undefined;

    const price = productModel?.formatPrice();
    const priceSubtitle =
      productModel && productModel.getQuantity() > 1
        ? productModel.displayPieces()
        : undefined;

    const videoSlug = mobile ? videoMobile || video : video;
    const ctaLabel = getSubmitLabel(false, t("Je commande", "landing"));

    const renderMedia = (
      videoRef?: React.MutableRefObject<HTMLVideoElement | null>
    ) => {
      if (videoSlug) {
        return (
          <SushishopVideo
            wrapperStyles={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
            poster={true}
            customPosterImage={{ desktop: imageUrl, mobile: imageMobileUrl }}
            ref={(ref) => {
              const innerVideoRef = ref?.getVideoRef();
              if (innerVideoRef && videoRef) {
                videoRef.current = innerVideoRef;
              }
            }}
            slug={videoSlug}
            resize="full"
            controls={false}
            autoPlay={false}
            loop={true}
            muted={true}
            playsInline
            // LCP-FIX: lazyLoad=true → <source> montés seulement au scroll (200px rootMargin),
            // évite fetchs MP4 concurrents en début de navigation sur listing pages.
            lazyLoad
            preload="none"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        );
      }
      if (imageUrl) {
        return (
          <img
            src={imageUrl}
            alt={name || ""}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        );
      }
      return null;
    };

    return (
      <>
        <style
          dangerouslySetInnerHTML={{
            __html: `
              .widget-instance[data-template="signature-chef"] {
                z-index: 6; 
                position: relative;
                margin-bottom: 8px;
              }
            `,
          }}
        />
        <SignatureChefWidget
          title={title}
          tag={tag}
          name={name}
          description={description}
          price={price}
          priceSubtitle={priceSubtitle}
          ctaLabel={ctaLabel}
          videoSlug={videoSlug}
          renderMedia={renderMedia}
          handleClick={handleClick}
          fromMentionLabel={fromMentionLabel}
          shouldDisplayPrice={shouldDisplayPrice}
        />
      </>
    );
  });

export default SignatureChef;
